import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Modal, Table, Switch, Input, Select, Tag, Space, Typography, message, Badge } from 'antd';
import { SearchOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import { GetJutgeCaza } from '../../../../dao/jutge/GetJutgeCaza';
import { GetConjuntPildoresCaza } from '../../../../dao/jutge/GetConjuntPildoresCaza';
import { AuthContext } from '../../../../config/AuthContext';

const { Text } = Typography;

interface Props {
    visible: boolean;
    onClose: () => void;
    conjunt: any;
    onUpdated?: () => void;
}

const ConfigurarPildoresConjuntModal: React.FC<Props> = ({ visible, onClose, conjunt, onUpdated }) => {
    const { userId } = useContext(AuthContext);
    const [cazas, setCazas] = useState<any[]>([]);
    const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterAssigned, setFilterAssigned] = useState<'all' | 'assigned' | 'unassigned'>('all');
    const [filterDificultat, setFilterDificultat] = useState<string>('all');
    const [updatingId, setUpdatingId] = useState<number | null>(null);

    const conjuntId = conjunt?.id;

    const loadData = async () => {
        if (!visible) return;
        setLoading(true);
        try {
            // All available pildores + the ones already linked to THIS conjunt (N:M)
            const [all, assigned] = await Promise.all([
                GetJutgeCaza(userId),
                conjuntId ? GetConjuntPildoresCaza(String(conjuntId), userId) : Promise.resolve([])
            ]);
            setCazas(Array.isArray(all) ? all : []);
            const ids = new Set<number>(
                (Array.isArray(assigned) ? assigned : []).map((c: any) => Number(c.id))
            );
            setAssignedIds(ids);
        } catch (e) {
            message.error('Error al carregar les píldores');
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, [visible, conjuntId]);

    const handleToggle = async (caza: any, checked: boolean) => {
        setUpdatingId(caza.id);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${caza.id}/conjunt`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_conjunt: conjuntId, assigned: checked })
            });
            if (res.ok) {
                message.success(checked ? 'Píldora afegida al conjunt' : 'Píldora eliminada del conjunt');
                setAssignedIds(prev => {
                    const next = new Set(prev);
                    if (checked) next.add(Number(caza.id)); else next.delete(Number(caza.id));
                    return next;
                });
                if (onUpdated) onUpdated();
            } else {
                message.error('Error al canviar la pertinença de la píldora');
            }
        } catch (e) {
            message.error('Error de connexió');
        }
        setUpdatingId(null);
    };

    const filtered = useMemo(() => {
        return cazas.filter(c => {
            const isAssignedToThis = assignedIds.has(Number(c.id));
            if (filterAssigned === 'assigned' && !isAssignedToThis) return false;
            if (filterAssigned === 'unassigned' && isAssignedToThis) return false;
            if (filterDificultat !== 'all' && c.dificultad !== filterDificultat) return false;

            if (searchText.trim()) {
                const q = searchText.toLowerCase();
                const match = (c.nombre && c.nombre.toLowerCase().includes(q)) ||
                    (c.descripcion && c.descripcion.toLowerCase().includes(q)) ||
                    String(c.id).includes(q);
                if (!match) return false;
            }
            return true;
        });
    }, [cazas, assignedIds, filterAssigned, filterDificultat, searchText]);

    const assignedCount = assignedIds.size;

    const columns: any[] = [
        {
            title: 'Assignada',
            key: 'assignada',
            width: 110,
            align: 'center' as const,
            render: (_: any, record: any) => {
                const isAssigned = assignedIds.has(Number(record.id));
                return (
                    <Switch
                        checked={isAssigned}
                        loading={updatingId === record.id}
                        onChange={checked => handleToggle(record, checked)}
                        checkedChildren="Dins"
                        unCheckedChildren="Fora"
                    />
                );
            }
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            align: 'center' as const,
            render: (id: number) => <Tag>#{id}</Tag>
        },
        {
            title: 'Píldora',
            key: 'nombre',
            render: (_: any, record: any) => (
                <div>
                    <Text strong>{record.nombre}</Text>
                    {record.descripcion && (
                        <div style={{ maxWidth: '100%' }}>
                            <Text type="secondary" style={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block' }}>
                                {record.descripcion}
                            </Text>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Dificultat',
            key: 'dificultad',
            width: 120,
            align: 'center' as const,
            render: (_: any, record: any) => {
                const color = record.dificultad === 'Beginner' ? 'green' : record.dificultad === 'Intermediate' ? 'orange' : 'red';
                return <Tag color={color}>{record.dificultad}</Tag>;
            }
        },
        {
            title: 'Aula',
            key: 'aula',
            width: 120,
            render: (_: any, record: any) => (
                record.aula ? <Tag color="blue">{record.aula}</Tag> : <Tag>Global</Tag>
            )
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    <MedicineBoxOutlined style={{ color: '#722ed1', fontSize: 20 }} />
                    <span>Configurar Píldores de: <strong>{conjunt?.nom || 'Conjunt'}</strong></span>
                    <Badge count={`${assignedCount} assignades`} style={{ backgroundColor: '#722ed1' }} />
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={850}
            destroyOnClose
        >
            <Space wrap style={{ marginBottom: 16, marginTop: 8 }}>
                <Input
                    placeholder="Cercar píldora..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 220 }}
                    allowClear
                />
                <Select
                    value={filterAssigned}
                    onChange={setFilterAssigned}
                    style={{ width: 180 }}
                    options={[
                        { label: 'Totes les píldores', value: 'all' },
                        { label: `Només assignades (${assignedCount})`, value: 'assigned' },
                        { label: 'No assignades', value: 'unassigned' }
                    ]}
                />
                <Select
                    value={filterDificultat}
                    onChange={setFilterDificultat}
                    style={{ width: 160 }}
                    options={[
                        { label: 'Tota dificultat', value: 'all' },
                        { label: 'Beginner', value: 'Beginner' },
                        { label: 'Intermediate', value: 'Intermediate' },
                        { label: 'Expert', value: 'Expert' }
                    ]}
                />
            </Space>

            <Table
                columns={columns}
                dataSource={filtered}
                rowKey={r => String(r.id)}
                size="small"
                loading={loading}
                pagination={{ pageSize: 8 }}
            />
        </Modal>
    );
};

export default ConfigurarPildoresConjuntModal;
